<template>
  <NuxtLayout name="sticky">
    <template #sticky>
      <TContainer :mobile-full="true">
        <div class="grid grid-cols-8 gap-2 lg:gap-1">
          <div class="col-span-8 lg:col-span-6">
            <CottageListSearch
              :model-value="locationName"
              @location-selected="handleLocationChange"
            />
          </div>
          <UButton
            size="xl"
            icon="i-heroicons-adjustments-horizontal-20-solid"
            color="neutral"
            variant="outline"
            class="col-span-4 lg:col-span-1"
            data-test="filters-button"
            @click="toggleFilters"
          >
            {{ t('views.cottage-list.results.buttons.filters') }}
          </UButton>
          <UButton
            size="xl"
            icon="i-heroicons-map-pin-20-solid"
            color="neutral"
            variant="outline"
            class="col-span-4 lg:col-span-1"
            data-test="map-button"
            @click="toggleMap"
          >
            {{ t('views.cottage-list.results.buttons.map') }}
          </UButton>
        </div>
      </TContainer>
    </template>

    <template #top>
      <ConnectifTag :name="CONNECTIF_PAGE_TAGS.LIST" />
      <ConnectifTag :name="CONNECTIF_CONTENT_TAGS.TRANSACTIONAL" />
      <ConnectifCategory :name="getConnectifCategoryName()" />
      <CottageListAlerts
        :cottages-data="cottagesData"
        class="mb-4"
        @toggle-filters="toggleFilters"
        @online-booking-filter="handleOnlineBookingFilter"
        @exact-capacity-filter="handleExactCapacityFilter"
        @remove-filter="handleRemoveFilter"
      />
      <div
        v-if="showPageH1 || showSortSelect"
        class="flex flex-wrap md:flex-nowrap items-center justify-between gap-4 mb-4"
      >
        <TText
          v-if="showPageH1"
          tag="h1"
          size="lg"
          variant="semibold"
          classes="mb-0 w-full"
        >
          {{ pageH1Heading }}
        </TText>

        <USelect
          v-if="showSortSelect"
          v-model="sortValue"
          :items="sortOptions"
          label-key="label"
          value-key="value"
          size="lg"
          class="w-full md:w-min"
          :ui="{ content: 'min-w-fit' }"
        />
      </div>
    </template>

    <UModal
      v-model:open="showFilters"
      :fullscreen="isMobile"
      :ui="filtersModal"
    >
      <template #body>
        <CottageListFilters
          :current-location-name="locationName"
          :current-filters="currentFiltersData"
          @submit="handleFiltersSubmit"
          @close="toggleFilters"
        />
      </template>
    </UModal>

    <TContainer>
      <UPageList v-if="cottagesData?.cottages?.length">
        <CottageListCard
          v-for="item in cottagesData.cottages"
          :key="item.id"
          :cottage="item"
        />
      </UPageList>
      <div
        v-if="cottagesData?.nearCottages?.length"
        data-test="near-cottages-section"
      >
        <UAlert
          v-if="showNearCottagesAlert"
          :title="
            cottagesData?.cottages?.length > 0
              ? t('views.cottage-list.results.alert.nearCottages.withNear', {
                  n: formattedNearCottagesTotal,
                  l: locationName,
                })
              : t('views.cottage-list.results.alert.nearCottages.onlyNear', {
                  l: locationName,
                })
          "
          color="info"
          variant="subtle"
          icon="i-heroicons-information-circle"
          orientation="horizontal"
          class="my-8"
        />
        <TText
          v-if="showNearCottagesHeading"
          :tag="parseInt(formattedTotalCottages) > 0 ? 'h2' : 'h1'"
          size="lg"
          variant="semibold"
          classes="my-4"
        >
          {{
            t('views.cottage-list.results.heading.nearCottages', {
              n: formattedNearCottagesTotal,
              l: locationName,
            })
          }}
        </TText>
        <UPageList>
          <CottageListCard
            v-for="item in cottagesData.nearCottages"
            :key="item.id"
            :cottage="item"
            with-distance
            :current-location-name="locationName"
          />
        </UPageList>
      </div>

      <div
        v-if="
          cottagesData?.cottages?.length || cottagesData?.nearCottages?.length
        "
        class="w-full flex flex-col items-center mt-8 gap-2"
      >
        <UPagination
          v-model:page="currentPage"
          :items-per-page="itemsPerPage"
          :total="cottagesMeta?.totalItems || 0"
        >
          <template #default="{ page, active }">
            <UButton
              :variant="active ? 'solid' : 'ghost'"
              :color="active ? 'primary' : 'neutral'"
              size="sm"
              @click="handlePageFilter(page)"
            >
              {{ page }}
            </UButton>
          </template>
        </UPagination>
        <TText size="sm" classes="text-neutral-500 mt-6">
          {{ paginationText }}
        </TText>
      </div>
      <div
        v-if="
          !cottagesData?.cottages?.length && !cottagesData?.nearCottages?.length
        "
        class="flex flex-col items-center justify-center py-12 text-center"
      >
        <UPageSection
          icon="i-heroicons-home"
          :title="t('views.cottage-list.results.empty.title')"
          :description="t('views.cottage-list.results.empty.description')"
        />
      </div>
    </TContainer>
    <slot> </slot>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { useCottageListResults } from '~~/modules/views/cottage-list/composables/useCottageListResults'
import { filtersModal } from '~~/modules/design-system/utils/nuxt-ui-config/components/modal'
import { ITEMS_PER_PAGE } from '~~/modules/views/cottage-list/constants'
import type { schemaType } from '~~/modules/api/rurality/v3/search/cottages/schema'
import type { MetaSuccessRuralitySchemaType } from '~~/modules/api/utils/rurality/v3/fetch/ruralitySchema'
import {
  CONNECTIF_PAGE_TAGS,
  CONNECTIF_CONTENT_TAGS,
} from '~~/modules/integrations/connectif/constants'

const itemsPerPage = ITEMS_PER_PAGE

const props = defineProps<{
  cottagesData: schemaType
  cottagesMeta: MetaSuccessRuralitySchemaType
  locationName: string
}>()

const { isMobile } = useDevice()

const {
  currentPage,
  sortValue,
  sortOptions,
  formattedTotalCottages,
  formattedNearCottagesTotal,
  currentFiltersData,
  paginationText,
  showFilters,
  showPageH1,
  showSortSelect,
  pageH1Heading,
  showNearCottagesAlert,
  showNearCottagesHeading,
  handleLocationChange,
  handleOnlineBookingFilter,
  handleExactCapacityFilter,
  handleRemoveFilter,
  handleFiltersSubmit,
  toggleFilters,
  toggleMap,
  handlePageFilter,
} = useCottageListResults({
  cottagesData: props.cottagesData,
  cottagesMeta: props.cottagesMeta,
  locationName: props.locationName,
})

const getConnectifCategoryName = () => {
  if (props.cottagesData.destination) {
    if (['province', 'region'].includes(props.cottagesData.destination.type)) {
      return props.cottagesData.destination.name
    }
    return props.cottagesData.destination.parentName
  }
  return ''
}
</script>
